import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
    HiOutlineCash, HiOutlineClipboardCheck, HiCheckCircle, HiOutlineRefresh, HiOutlineEmojiSad, HiArrowRight,
    HiOutlineShoppingBag, HiOutlineCalendar, HiOutlineCurrencyRupee, HiOutlineReceiptTax, HiOutlineUsers,
    HiOutlineUserAdd, HiOutlineLightningBolt, HiOutlineCollection, HiOutlineCube, HiOutlineExclamation,
    HiOutlineEyeOff, HiOutlineViewGridAdd, HiOutlinePlusCircle, HiTrendingUp, HiTrendingDown, HiOutlineScale
} from 'react-icons/hi';
import DashboardService from '../service/DashboardService.js';
import AuthenticationService from '../service/AuthenticationService.js';
import BarChart from '../components/BarChart';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { formatRupee } from '../components/Price';
import { statusLabel, statusTone } from '../util/orderStatus';
import { categoryOf } from '../util/expenseTypes';
import '../css/admin.css'
import '../css/dashboard.css'

const RANGES = [7, 14, 30]
const PIPELINE = [
    { key: 'CONFIRMED', code: 1 },
    { key: 'PROCESSING', code: 2 },
    { key: 'PACKAGED', code: 3 },
    { key: 'SHIPPED', code: 4 },
    { key: 'DELIVERED', code: 5 }
]

const count = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? n.toLocaleString('en-IN') : '–'
}

// "2026-10-08" -> local date (no timezone shift)
const parseDay = (iso) => {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d)
}

const dayTitle = (iso) => parseDay(iso).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
const dayAxis = (iso, range) => range <= 7
    ? parseDay(iso).toLocaleDateString('en-IN', { weekday: 'short' })
    : parseDay(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })

// createdAt and serverTime are both the server's local clock (no timezone), so compare them with each other,
// not with this browser's clock
const timeAgo = (iso, serverTime) => {
    if (!iso) return ''
    const now = serverTime ? new Date(serverTime).getTime() : Date.now()
    const mins = Math.round((now - new Date(iso).getTime()) / 60000)
    if (!Number.isFinite(mins)) return ''
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins} min ago`
    if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`
    return new Date(Date.now() - mins * 60000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

const greeting = () => {
    const h = new Date().getHours()
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
}

const sum = (list, field) => list.reduce((total, d) => total + (Number(d[field]) || 0), 0)

// % change vs the previous period; null when there is nothing to compare with
const change = (current, previous) => {
    if (previous === null || previous === undefined) return null
    if (previous === 0) return current > 0 ? 100 : 0
    return Math.round(((current - previous) / previous) * 100)
}

const Trend = ({ value }) => {
    if (value === null) return null
    const up = value > 0
    const flat = value === 0
    return (
        <span className={'mv-dash-trend' + (flat ? ' is-flat' : up ? ' is-up' : ' is-down')}>
            {!flat && (up ? <HiTrendingUp aria-hidden='true' /> : <HiTrendingDown aria-hidden='true' />)}
            {flat ? 'no change' : `${Math.abs(value)}%`}
            <span className='mv-visually-hidden'>{up ? ' up' : flat ? '' : ' down'} on the previous period</span>
        </span>
    )
}

const Kpi = ({ icon, label, value, note, trend, tone = 'primary', loading }) => (
    <div className={'mv-kpi mv-kpi-' + tone}>
        <span className='mv-kpi-icon' aria-hidden='true'>{icon}</span>
        <span className='mv-kpi-label'>{label}</span>
        {loading ? <Skeleton width='60%' height={28} /> :
            <span className='mv-kpi-value'>{value}</span>}
        {!loading && (note || trend !== undefined) &&
            <span className='mv-kpi-note'>{trend !== undefined && <Trend value={trend} />}{note}</span>}
    </div>
)

// horizontal single-hue bars with the value at the end (team mix, best sellers, top costs)
const HBars = ({ rows, unit, format = count }) => {
    const max = Math.max(1, ...rows.map(r => r.value))
    return (
        <ul className='mv-hbars'>
            {rows.map(r =>
                <li key={r.key} className='mv-hbar'>
                    <span className='mv-hbar-label'>{r.label}</span>
                    <span className='mv-hbar-track'>
                        <span className='mv-hbar-fill' style={{ width: `${Math.max(r.value > 0 ? 2 : 0, (r.value / max) * 100)}%` }} />
                    </span>
                    <span className='mv-hbar-value'>{format(r.value)}{unit ? ` ${unit}` : ''}</span>
                </li>
            )}
        </ul>
    )
}

const SegmentHead = ({ n, id, title, sub, action }) => (
    <div className='mv-dash-seg-head'>
        <span className='mv-dash-seg-num' aria-hidden='true'>{n}</span>
        <div className='mv-dash-seg-text'>
            <h2 id={id}>{title}</h2>
            {sub && <p>{sub}</p>}
        </div>
        {action}
    </div>
)

const AdminDashboard = () => {
    const history = useHistory();

    const [data, setData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [range, setRange] = useState(14)

    const canManageSettings = AuthenticationService.canManageSettings()
    const roleLabel = AuthenticationService.getRole() === 'MANAGER' ? 'Manager' : 'Admin'
    const userName = AuthenticationService.getAuthUserName()

    const loadData = () => {
        setIsLoading(true)
        setLoadError(false)
        DashboardService.getDashboardData().then(res => {
            if (res && res.data) setData(res.data.data)
            setIsLoading(false)
        }).catch(error => {
            if (error?.response?.status === 401) {
                history.push('/login')
                return
            }
            setLoadError(true)
            setIsLoading(false)
        });
    }

    useEffect(() => {
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const d = data || {}
    const loading = isLoading && !data
    const showMoney = d.revenue30d !== undefined && d.revenue30d !== null
    const days = d.ordersByDay || []
    const inRange = days.slice(-range)
    const previous = days.length >= range * 2 ? days.slice(-range * 2, -range) : null
    const rangeOrders = sum(inRange, 'count')
    const rangeRevenue = sum(inRange, 'revenue')
    const rangeExpenses = sum(inRange, 'expenses')
    const rangeNet = rangeRevenue - rangeExpenses
    const showProfit = showMoney && d.expenses30d !== undefined && d.expenses30d !== null
    const seg = (n) => String(showProfit || n < 2 ? n : n - 1)
    const toVerify = Number(d.paymentsToVerify) || 0
    const confirmed = Number(d.statusCounts && d.statusCounts.CONFIRMED) || 0
    const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })
    const lastDay = inRange.length ? inRange[inRange.length - 1].date : null

    const chartData = inRange.map(day => ({
        key: day.date,
        label: dayAxis(day.date, range),
        title: dayTitle(day.date),
        value: day.count,
        detail: showMoney && day.count > 0 ? formatRupee(day.revenue) : ''
    }))
    const busiest = chartData.reduce((best, day) => (!best || day.value > best.value ? day : best), null)

    const openDeliveries = (search) => history.push({ pathname: '/admin/orders', search })

    return (
        <main className='mv-page mv-dash'>
            <header className='mv-dash-head'>
                <div>
                    <h1 className='mv-dash-title'>{greeting()}{userName ? `, ${userName}` : ''}</h1>
                    <p className='mv-dash-sub'>
                        <span>{today}</span>
                        <span className='mv-dash-role'>{roleLabel}</span>
                    </p>
                </div>
                <button type='button' className={'mv-btn mv-btn-secondary mv-btn-sm' + (isLoading && data ? ' is-busy' : '')}
                    onClick={loadData} disabled={isLoading}>
                    {!(isLoading && data) && <HiOutlineRefresh aria-hidden='true' />} Refresh
                </button>
            </header>

            {loadError &&
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load the dashboard'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={loadData} />}

            {!loadError && <>

                {/* ============ 1 · Orders ============ */}
                <section className='mv-dash-seg' aria-labelledby='seg-orders'>
                    <SegmentHead n='1' id='seg-orders' title='Orders' sub='What needs doing now, and how sales are going'
                        action={<Link className='mv-pill-link' to='/admin/orders'>Deliveries <HiArrowRight aria-hidden='true' /></Link>} />

                    {loading ?
                        <div className='mv-dash-todo'><Skeleton height={96} radius='16px' /><Skeleton height={96} radius='16px' /></div>
                        : (toVerify === 0 && confirmed === 0) ?
                            <div className='mv-dash-clear'>
                                <HiCheckCircle aria-hidden='true' />
                                <div><strong>All caught up</strong><span>No payments to verify and no new orders waiting.</span></div>
                            </div>
                            :
                            <div className='mv-dash-todo'>
                                <button type='button' className={'mv-todo mv-todo-pay' + (toVerify ? '' : ' is-done')}
                                    onClick={() => openDeliveries('?view=payments')}>
                                    <span className='mv-todo-icon' aria-hidden='true'><HiOutlineCash /></span>
                                    <span className='mv-todo-body'>
                                        <span className='mv-todo-count'>{count(toVerify)}</span>
                                        <span className='mv-todo-title'>Payments to verify</span>
                                        <span className='mv-todo-text'>
                                            {toVerify === 0 ? 'Nothing waiting' :
                                                showMoney && d.paymentsToVerifyAmount ? `${formatRupee(d.paymentsToVerifyAmount)} waiting for approval` : 'Check the payment and approve'}
                                        </span>
                                    </span>
                                    <HiArrowRight className='mv-todo-arrow' aria-hidden='true' />
                                </button>
                                <button type='button' className={'mv-todo mv-todo-orders' + (confirmed ? '' : ' is-done')}
                                    onClick={() => openDeliveries('?status=1')}>
                                    <span className='mv-todo-icon' aria-hidden='true'><HiOutlineClipboardCheck /></span>
                                    <span className='mv-todo-body'>
                                        <span className='mv-todo-count'>{count(confirmed)}</span>
                                        <span className='mv-todo-title'>Confirmed orders</span>
                                        <span className='mv-todo-text'>{confirmed === 0 ? 'Nothing waiting' : 'Paid and ready to process'}</span>
                                    </span>
                                    <HiArrowRight className='mv-todo-arrow' aria-hidden='true' />
                                </button>
                            </div>}

                    <div className='mv-dash-orders-grid'>
                        <div className='mv-kpi-grid'>
                            <Kpi icon={<HiOutlineShoppingBag />} label='Orders today' value={count(d.ordersToday)} loading={loading}
                                note={showMoney && d.revenueToday ? formatRupee(d.revenueToday) : undefined} />
                            <Kpi icon={<HiOutlineCalendar />} label={`Orders · last ${range} days`} value={count(rangeOrders)} loading={loading}
                                trend={previous ? change(rangeOrders, sum(previous, 'count')) : undefined} tone='sky' />
                            {showMoney && <>
                                <Kpi icon={<HiOutlineCurrencyRupee />} label={`Revenue · last ${range} days`} value={formatRupee(rangeRevenue) || '₹0'}
                                    loading={loading} trend={previous ? change(rangeRevenue, sum(previous, 'revenue')) : undefined} tone='leaf' />
                                <Kpi icon={<HiOutlineReceiptTax />} label='Average order' tone='accent' loading={loading}
                                    value={rangeOrders ? formatRupee(rangeRevenue / rangeOrders) : '–'} />
                            </>}
                        </div>

                        <div className='mv-dash-card mv-dash-chart'>
                            <div className='mv-dash-card-head'>
                                <div>
                                    <h3>Orders per day</h3>
                                    {!loading && busiest && busiest.value > 0 &&
                                        <p>Busiest: {busiest.title} · {busiest.value} {busiest.value === 1 ? 'order' : 'orders'}</p>}
                                </div>
                                <div className='mv-range' role='group' aria-label='Time range'>
                                    {RANGES.map(r =>
                                        <button key={r} type='button' aria-pressed={range === r}
                                            className={'mv-range-btn' + (range === r ? ' active' : '')} onClick={() => setRange(r)}>
                                            {r}d
                                        </button>
                                    )}
                                </div>
                            </div>
                            {loading ? <Skeleton height={220} radius='12px' /> :
                                <BarChart data={chartData} unit='orders' height={220}
                                    labelKeys={[lastDay, busiest && busiest.key].filter(Boolean)}
                                    ariaLabel={`Orders per day for the last ${range} days`} />}
                        </div>
                    </div>

                    <div className='mv-dash-card'>
                        <div className='mv-dash-card-head'>
                            <h3>Order pipeline</h3>
                            {!loading && Number(d.statusCounts && d.statusCounts.CANCELLED) > 0 &&
                                <span className='mv-status mv-status-danger'>{count(d.statusCounts.CANCELLED)} cancelled</span>}
                        </div>
                        <ol className='mv-pipeline'>
                            {PIPELINE.map(step =>
                                <li key={step.key}>
                                    <button type='button' className={'mv-pipe-step mv-pipe-' + statusTone(step.key)}
                                        onClick={() => openDeliveries('?status=' + step.code)}>
                                        <span className='mv-pipe-count'>{loading ? '–' : count(d.statusCounts && d.statusCounts[step.key])}</span>
                                        <span className='mv-pipe-label'>{statusLabel(step.key)}</span>
                                    </button>
                                </li>
                            )}
                        </ol>
                    </div>

                    <div className='mv-dash-card'>
                        <div className='mv-dash-card-head'>
                            <h3>Latest orders</h3>
                        </div>
                        {loading ?
                            <div className='mv-stack'>{[0, 1, 2].map(i => <Skeleton key={i} height={48} radius='10px' />)}</div>
                            : (d.recentOrders || []).length === 0 ?
                                <p className='mv-dash-empty'>No orders yet. New orders will show up here.</p>
                                :
                                <ul className='mv-recent'>
                                    {d.recentOrders.map(o => {
                                        const status = PIPELINE.find(p => p.code === o.status)?.key || ({ 6: 'CANCELLED', 7: 'REQUEST_RETURN', 8: 'RETURNED' })[o.status] || ''
                                        return (
                                            <li key={o.id}>
                                                <button type='button' className='mv-recent-row'
                                                    onClick={() => history.push({ pathname: '/orders/admin/view/' + o.id, state: { id: o.id } })}>
                                                    <span className='mv-recent-avatar' aria-hidden='true'>{(o.customer || '?').trim().charAt(0).toUpperCase()}</span>
                                                    <span className='mv-recent-name'>{o.customer || 'Customer'}</span>
                                                    <span className='mv-recent-meta'>{timeAgo(o.createdAt, d.serverTime)} · {o.items} {o.items === 1 ? 'item' : 'items'}</span>
                                                    {status && <span className={`mv-status mv-recent-status mv-status-${statusTone(status)}`}>{statusLabel(status)}</span>}
                                                    {showMoney && <span className='mv-recent-amount'>{formatRupee(o.amount)}</span>}
                                                </button>
                                            </li>
                                        )
                                    })}
                                </ul>}
                    </div>
                </section>

                {/* ============ 2 · Profit (admins) ============ */}
                {showProfit &&
                    <section className='mv-dash-seg' aria-labelledby='seg-profit'>
                        <SegmentHead n='2' id='seg-profit' title='Profit' sub={`Sales minus expenses · last ${range} days`}
                            action={<Link className='mv-pill-link' to='/admin/expenses'>Expenses <HiArrowRight aria-hidden='true' /></Link>} />

                        <div className='mv-dash-two'>
                            <div className='mv-kpi-grid'>
                                <Kpi icon={<HiOutlineCurrencyRupee />} label='Sales' value={formatRupee(rangeRevenue) || '₹0'} loading={loading} tone='sky' />
                                <Kpi icon={<HiOutlineCash />} label='Expenses' value={formatRupee(rangeExpenses) || '₹0'} loading={loading}
                                    note={rangeRevenue > 0 ? `${Math.round((rangeExpenses / rangeRevenue) * 100)}% of sales` : undefined} />
                                <Kpi icon={rangeNet >= 0 ? <HiTrendingUp /> : <HiTrendingDown />} label={rangeNet >= 0 ? 'Net · profit' : 'Net · loss'}
                                    value={(rangeNet < 0 ? '−' : '') + (formatRupee(Math.abs(rangeNet)) || '₹0')} loading={loading}
                                    tone={rangeNet >= 0 ? 'leaf' : 'danger'} />
                                <Kpi icon={<HiOutlineScale />} label='Margin' tone='accent' loading={loading}
                                    value={rangeRevenue > 0 ? `${Math.round((rangeNet / rangeRevenue) * 100)}%` : '–'}
                                    note={rangeRevenue > 0 ? 'Net as a share of sales' : 'No sales yet'} />
                            </div>

                            <div className='mv-dash-card'>
                                <div className='mv-dash-card-head'>
                                    <h3>Top costs · last 30 days</h3>
                                    {!loading && <span className='mv-dash-card-note'>{formatRupee(d.expenses30d) || '₹0'} spent</span>}
                                </div>
                                {loading ? <Skeleton height={120} radius='10px' /> :
                                    (d.topExpenseCategories || []).length === 0 ?
                                        <p className='mv-dash-empty'>No expenses in the last 30 days. <Link to='/admin/expenses'>Add one</Link></p> :
                                        <HBars format={formatRupee} rows={d.topExpenseCategories.map(c => ({ key: c.key, label: categoryOf(c.key).label, value: Number(c.amount) || 0 }))} />}
                            </div>
                        </div>
                    </section>}

                {/* ============ 3 · Users ============ */}
                <section className='mv-dash-seg' aria-labelledby='seg-users'>
                    <SegmentHead n={seg(3)} id='seg-users' title='Users' sub='Who is shopping and who runs the shop'
                        action={canManageSettings && <Link className='mv-pill-link' to='/admin/users'>Manage users <HiArrowRight aria-hidden='true' /></Link>} />

                    <div className='mv-dash-two'>
                        <div className='mv-kpi-grid mv-kpi-grid-3'>
                            <Kpi icon={<HiOutlineUsers />} label='Total users' value={count(d.totalUsers)} loading={loading} tone='sky'
                                note={!loading && d.disabledUsers > 0 ? `${count(d.disabledUsers)} disabled` : undefined} />
                            <Kpi icon={<HiOutlineUserAdd />} label='New this week' value={count(d.newUsers)} loading={loading} tone='leaf' />
                            <Kpi icon={<HiOutlineLightningBolt />} label='Active this week' value={count(d.activeUsers7d)} loading={loading} tone='accent'
                                note='Signed in' />
                        </div>

                        <div className='mv-dash-card'>
                            <div className='mv-dash-card-head'><h3>Sign-ups · last 14 days</h3></div>
                            {loading ? <Skeleton height={64} radius='10px' /> :
                                <BarChart compact height={64} unit='sign-ups' ariaLabel='Sign-ups per day for the last 14 days'
                                    data={(d.newUsersByDay || []).map(day => ({ key: day.date, label: dayAxis(day.date, 14), title: dayTitle(day.date), value: day.count }))} />}
                            <div className='mv-dash-card-head mv-dash-card-sub'><h3>Team mix</h3></div>
                            {loading ? <Skeleton height={90} radius='10px' /> :
                                <HBars rows={[
                                    { key: 'c', label: 'Customers', value: Number(d.customers) || 0 },
                                    { key: 'm', label: 'Managers', value: Number(d.managers) || 0 },
                                    { key: 'a', label: 'Admins', value: Number(d.admins) || 0 }
                                ]} />}
                        </div>
                    </div>
                </section>

                {/* ============ 4 · Inventory ============ */}
                <section className='mv-dash-seg' aria-labelledby='seg-inventory'>
                    <SegmentHead n={seg(4)} id='seg-inventory' title='Inventory' sub='Stock health and what is selling'
                        action={<div className='mv-dash-seg-actions'>
                            <Link className='mv-pill-link' to='/shop/item/create'><HiOutlinePlusCircle aria-hidden='true' /> Add product</Link>
                            <Link className='mv-pill-link' to='/shop/inventory'><HiOutlineViewGridAdd aria-hidden='true' /> Inventory</Link>
                        </div>} />

                    <div className='mv-kpi-grid mv-kpi-grid-4'>
                        <Kpi icon={<HiOutlineCollection />} label='Products' value={count(d.totalItems)} loading={loading} />
                        <Kpi icon={<HiOutlineCube />} label='Units in stock' value={count(d.totalAllItems)} loading={loading} tone='sky' />
                        <Kpi icon={<HiOutlineExclamation />} label='Out of stock' value={count(d.outOfStockItems)} loading={loading}
                            tone={Number(d.outOfStockItems) > 0 ? 'danger' : 'leaf'} note={!loading ? 'On sale, no sizes left' : undefined} />
                        <Kpi icon={<HiOutlineEyeOff />} label='Hidden from shop' value={count(d.hiddenItems)} loading={loading} tone='muted' />
                    </div>

                    <div className='mv-dash-two'>
                        <div className='mv-dash-card'>
                            <div className='mv-dash-card-head'>
                                <h3>Restock soon</h3>
                                {!loading && Number(d.lowStockSizes) > 0 &&
                                    <span className='mv-status mv-status-warning'>{count(d.lowStockSizes)} {Number(d.lowStockSizes) === 1 ? 'size' : 'sizes'} low</span>}
                            </div>
                            {loading ? <Skeleton height={120} radius='10px' /> :
                                (d.lowStock || []).length === 0 ?
                                    <p className='mv-dash-empty'><HiCheckCircle aria-hidden='true' /> Every size has more than 3 left.</p> :
                                    <ul className='mv-restock'>
                                        {d.lowStock.map((s, i) =>
                                            <li key={s.itemId + '-' + i}>
                                                <button type='button' className='mv-restock-row'
                                                    onClick={() => history.push({ pathname: '/shop/inventory/item', state: { id: s.itemId } })}>
                                                    <span className='mv-restock-name'>{s.itemName}</span>
                                                    {s.size && <span className='mv-restock-size'>{s.size}</span>}
                                                    <span className='mv-restock-left'>{s.quantity} left</span>
                                                </button>
                                            </li>
                                        )}
                                    </ul>}
                        </div>

                        <div className='mv-dash-card'>
                            <div className='mv-dash-card-head'><h3>Best sellers · last 30 days</h3></div>
                            {loading ? <Skeleton height={120} radius='10px' /> :
                                (d.topSellers || []).length === 0 ?
                                    <p className='mv-dash-empty'>No sales in the last 30 days yet.</p> :
                                    <HBars unit='sold' rows={d.topSellers.map(t => ({ key: t.itemId, label: t.itemName, value: Number(t.units) || 0 }))} />}
                        </div>
                    </div>
                </section>
            </>}
        </main>
    );
};

export default AdminDashboard;
